"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { apiRemote } from "@/api";
import { useAlert } from "@/contexts/AlertContext";
import { EditToggle, EditType } from "@/components/EditToggle";
import { useSiteAlert } from "@/contexts/SiteAlertContext";

export default function AlertForm() {
  const [message, setMessage] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<"show" | "hide">("show");
  const [loading, setLoading] = useState(false);
  const { reload } = useSiteAlert();

  const [loadingAlert, setLoadingAlert] = useState(true);
  const alert = useAlert();

  useEffect(() => {
    const fetchAlert = async () => {
      try {
        apiRemote.getSiteAlert().then((data) => {
          setMessage(data.data.message || "");
          setVisibility(data.data.is_active ? "show" : "hide");
          setLoadingAlert(false);
        });
      } catch (err) {
        console.error("Error loading alert:", err);
      }
    };

    fetchAlert();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message == "") {
      alert.error("Message is required");
      return false;
    }
    setLoading(true);
    try {
      const res = await apiRemote.updateSiteAlert({
        message: message,
        is_active: visibility === "show",
      });
      if (res.code == 200) {
        alert.success("Success");
        reload();
      } else {
        alert.success(res.message);
      }
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
      {loading ? (
        <div>Loading...</div> // hoặc spinner
      ) : (
        <>
          <form onSubmit={handleSubmit}>
            <CardHeader
              title="Update Site Alert"
              subheader="Edit the alert message and display status"
            />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              {message === null ? (
                <div>Loading...</div>
              ) : (
                <EditToggle
                  label="Message"
                  data={{
                    value: message || "",
                    type: EditType.EDITOR,
                  }}
                  isEdit={true}
                  required={true}
                  onChange={(value: string) => {
                    const trimmed = value?.trimStart() || "";
                    if (trimmed.length < 5000) {
                      setMessage(trimmed);
                    }
                  }}
                />
              )}

              <FormControl component="fieldset">
                <FormLabel component="legend">Display Status</FormLabel>
                <RadioGroup
                  value={visibility}
                  onChange={(e) =>
                    setVisibility(e.target.value as "show" | "hide")
                  }
                >
                  <FormControlLabel
                    value="show"
                    control={<Radio />}
                    label="Show"
                  />
                  <FormControlLabel
                    value="hide"
                    control={<Radio />}
                    label="Hide"
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
            <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
              {/* <Button variant="outlined" fullWidth sx={{ mr: 1 }}>
              Cancel
            </Button> */}
              <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Update"
                )}
              </Button>
            </CardActions>
          </form>
        </>
      )}
    </Card>
  );
}
